// AROMA COMPASS — Motore di scoring e generazione del risultato.

import { OILS, CATEGORY_TO_OIL, resolveOil } from "../data/oils";
import {
  findDefinedSynergy,
  findSubstitution,
  compatibilityStatus,
} from "../data/synergies";

export function computeScores(answers) {
  const totals = {};
  for (const a of answers) {
    if (!a) continue;
    for (const [cat, pts] of Object.entries(a.scores || {})) {
      totals[cat] = (totals[cat] || 0) + pts;
    }
  }
  return totals;
}

export function rankedCategories(totals) {
  return Object.entries(totals)
    .sort((a, b) => b[1] - a[1])
    .map(([cat, pts]) => ({ category: cat, points: pts }));
}

function oilFromCategory(cat) {
  const id = CATEGORY_TO_OIL[cat];
  return id ? { id, ...OILS[id] } : null;
}

export function pickTwoOils(ranked) {
  const seenOils = new Set();
  const picks = [];

  for (const r of ranked) {
    const oil = oilFromCategory(r.category);
    if (!oil) continue;
    if (seenOils.has(oil.id)) continue;

    if (picks.length === 1) {
      const status = compatibilityStatus(picks[0].id, oil.id);
      const sub = findSubstitution(picks[0].id, oil.id);
      if (status === "NOT_COMPATIBLE" && !sub) continue;
    }

    picks.push(oil);
    seenOils.add(oil.id);
    if (picks.length === 2) break;
  }

  const fallback = ["abete", "basilico", "limone", "lavanda", "arancio_dolce", "geranio"];
  for (const id of fallback) {
    if (picks.length === 2) break;
    if (seenOils.has(id)) continue;
    if (picks.length === 1) {
      const status = compatibilityStatus(picks[0].id, id);
      const sub = findSubstitution(picks[0].id, id);
      if (status === "NOT_COMPATIBLE" && !sub) continue;
    }
    picks.push({ id, ...OILS[id] });
    seenOils.add(id);
  }
  return picks;
}

function generateSynergyName(oil1, oil2) {
  return `${oil1.keyword} × ${oil2.keyword}`;
}

function defaultRitual(lang = "it") {
  if (lang === "en") {
    return "Bring the blend close. Take three slow breaths and let the aroma accompany your attention toward the chosen intention.";
  }
  return "Porta la sinergia vicino a te. Fai tre respiri lenti e lascia che l'aroma accompagni la tua attenzione verso l'intenzione scelta.";
}

function defaultInterpretation(oil1, oil2, lang = "it") {
  const eu = oil1.id === "eucalipto" ? oil1 : oil2.id === "eucalipto" ? oil2 : null;
  if (eu) {
    const other = eu === oil1 ? oil2 : oil1;
    if (lang === "en") {
      return `Today the mind seems crowded: thoughts, worries or agitation take up too much space. In Aroma Compass the «Cold» of Eucalipto is not temperature, but a symbolic freshness that cools what has become too intense and creates space, clarity, breath. In that space, ${other.name} can carry its quality of ${other.keyword.toLowerCase()} with more clarity.`;
    }
    return `Oggi la mente sembra affollarsi: pensieri, preoccupazioni o agitazione occupano troppo spazio. In Aroma Compass il «Freddo» di Eucalipto non è temperatura, ma la freschezza simbolica che raffredda ciò che è diventato troppo intenso e crea spazio, lucidità, respiro. In quello spazio, ${other.name} può portare la sua qualità di ${other.keyword.toLowerCase()} con più chiarezza.`;
  }
  const cam = oil1.id === "camomilla" ? oil1 : oil2.id === "camomilla" ? oil2 : null;
  if (cam) {
    const other = cam === oil1 ? oil2 : oil1;
    if (lang === "en") {
      return `Today something inside asks you to slow down. In Aroma Compass, Camomilla romana carries a quality of «slow mind»: it doesn't chase goals, it invites you to inhabit the moment one step at a time and to enjoy the journey more than the destination. Together with ${other.name}, which brings its quality of ${other.keyword.toLowerCase()}, it leads you back to a more personal pace.`;
    }
    return `Oggi qualcosa dentro chiede di rallentare. In Aroma Compass la Camomilla romana porta con sé una qualità di «slow mind»: non insegue obiettivi, invita ad abitare il momento un passo alla volta e a godersi il percorso più della meta. Insieme a ${other.name}, che apporta la sua qualità di ${other.keyword.toLowerCase()}, ti riporta a un ritmo più tuo.`;
  }
  if (lang === "en") {
    return `Today your compass unites ${oil1.name} and ${oil2.name}: two voices that together tell the movement you need. ${oil1.keyword} meets ${oil2.keyword} and draws a direction tailored to your moment.`;
  }
  return `Oggi la tua bussola unisce ${oil1.name} e ${oil2.name}: due voci che insieme raccontano il movimento di cui hai bisogno. ${oil1.keyword} incontra ${oil2.keyword} e disegna una direzione a misura del tuo momento.`;
}

function defaultIntention(oil1, oil2, lang = "it") {
  const eu = oil1.id === "eucalipto" ? oil1 : oil2.id === "eucalipto" ? oil2 : null;
  if (eu) {
    const other = eu === oil1 ? oil2 : oil1;
    if (lang === "en") {
      return `I create space within me and let ${other.keyword.toLowerCase()} be felt again with clarity.`;
    }
    return `Creo spazio dentro di me e lascio che ${other.keyword.toLowerCase()} torni a farsi sentire con chiarezza.`;
  }
  const cam = oil1.id === "camomilla" ? oil1 : oil2.id === "camomilla" ? oil2 : null;
  if (cam) {
    const other = cam === oil1 ? oil2 : oil1;
    if (lang === "en") {
      return `I slow down and let ${other.name} walk with me, one step at a time.`;
    }
    return `Rallento, e lascio che ${other.name} mi accompagni un passo alla volta.`;
  }
  if (lang === "en") {
    return `I welcome ${oil1.keyword.toLowerCase()} and ${oil2.keyword.toLowerCase()}, and let what I feel guide me.`;
  }
  const k1 = oil1.keyword.toLowerCase();
  const k2 = oil2.keyword.toLowerCase();
  return `Accolgo ${k1} e ${k2}, e mi lascio guidare da ciò che sento.`;
}

// Funzione principale: dalle risposte al risultato completo.
// `lang` scegli il testo di interpretazione/rituale/intenzione (default italiano).
export function generateResult(answers, lang = "it") {
  const totals = computeScores(answers);
  const ranked = rankedCategories(totals);
  let oils = pickTwoOils(ranked);

  const sub = findSubstitution(oils[0].id, oils[1].id);
  if (sub) {
    oils = sub.use.map((id) => ({ id, ...OILS[id] }));
  }

  const [oil1, oil2] = oils;
  const defined = findDefinedSynergy(oil1.id, oil2.id);

  // Risolvi i nomi/parole chiave nella lingua richiesta.
  const oilsLocalized = [resolveOil(oil1, lang), resolveOil(oil2, lang)];
  const [oil1Loc, oil2Loc] = oilsLocalized;

  const interpretation = defined
    ? (lang === "en" ? (defined.interpretation_en || defined.interpretation) : defined.interpretation)
    : defaultInterpretation(oil1Loc, oil2Loc, lang);
  const ritual = defined
    ? (lang === "en" ? (defined.ritual_en || defined.ritual) : defined.ritual)
    : defaultRitual(lang);
  const intention = defined
    ? (lang === "en" ? (defined.intention_en || defined.intention) : defined.intention)
    : defaultIntention(oil1Loc, oil2Loc, lang);
  const synergyName = defined
    ? (lang === "en" ? (defined.name_en || defined.name) : defined.name)
    : generateSynergyName(oil1Loc, oil2Loc);

  return {
    oils: oilsLocalized,
    keywords: [oil1Loc.keyword, oil2Loc.keyword],
    categories: ranked.slice(0, 2).map((r) => r.category),
    scores: totals,
    isOfficial: !!defined,
    name: synergyName,
    interpretation,
    ritual,
    intention,
  };
}
